import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Dropdown, Form, Spinner } from 'react-bootstrap';
import Icon from '../../common/Icons';
import {
  AdminPersonnelModal,
  AdminPersonnelPageHeader,
  ModalFooterActions
} from '../ui';
import { useApToast } from '../hooks/useApToast';
import invoiceService from '../../../services/invoiceService';
import CompanyService from '../../../services/companyService';
import SiteService from '../../../services/siteService';
import useSmieAffiliations from '../../../hooks/useSmieAffiliations';
import { AP_COPY } from '../adminPersonnelCopy';
import { formatInvoiceAmount } from '../pricing/actPricingUtils';
import { exportInvoicesToExcel, exportInvoicesToPdf } from './invoiceExport';
import '../AdminPersonnelPortal.css';
import './AdminPersonnelInvoicesPage.css';

const I = AP_COPY.INVOICES;
const formatActPrice = formatInvoiceAmount;
const PAGE_SIZE = 20;
const INVOICE_KIND = {
  CARE: 'CARE',
  COVERAGE: 'COVERAGE'
};

const SERVICE_OPTIONS = [
  { id: '', label: I.FILTER_SERVICE_ALL },
  { id: 'MEDICAL', label: 'Médicale' },
  { id: 'DENTAL', label: 'Dentaire' },
  { id: 'NURSING', label: 'Infirmerie' }
];

const DEFAULT_FILTERS = {
  search: '',
  affiliationId: '',
  companyId: '',
  siteId: '',
  serviceLine: '',
  dateFrom: '',
  dateTo: ''
};

function formatDate(value) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleDateString('fr-FR');
  } catch {
    return '—';
  }
}

export default function AdminPersonnelInvoicesPage() {
  const { showToast } = useApToast();
  const { affiliations } = useSmieAffiliations();
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(DEFAULT_FILTERS.search);
  const [affiliationId, setAffiliationId] = useState(DEFAULT_FILTERS.affiliationId);
  const [companyId, setCompanyId] = useState(DEFAULT_FILTERS.companyId);
  const [siteId, setSiteId] = useState(DEFAULT_FILTERS.siteId);
  const [invoiceKind, setInvoiceKind] = useState(INVOICE_KIND.CARE);
  const [serviceLine, setServiceLine] = useState(DEFAULT_FILTERS.serviceLine);
  const [dateFrom, setDateFrom] = useState(DEFAULT_FILTERS.dateFrom);
  const [dateTo, setDateTo] = useState(DEFAULT_FILTERS.dateTo);
  const [companies, setCompanies] = useState([]);
  const [sites, setSites] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [coRes, siteRes] = await Promise.all([
          CompanyService.getActiveCompanies(),
          SiteService.getActiveSites()
        ]);
        if (cancelled) return;
        const coList = coRes?.data || coRes || [];
        setCompanies(Array.isArray(coList) ? coList : []);
        const siteList = siteRes?.data || siteRes || [];
        setSites(Array.isArray(siteList) ? siteList : []);
      } catch (e) {
        console.error(e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const hasFilters =
    Boolean(search.trim()) ||
    Boolean(affiliationId) ||
    Boolean(companyId) ||
    Boolean(siteId) ||
    Boolean(serviceLine) ||
    Boolean(dateFrom) ||
    Boolean(dateTo);

  const clearFilters = useCallback(() => {
    setSearch(DEFAULT_FILTERS.search);
    setAffiliationId(DEFAULT_FILTERS.affiliationId);
    setCompanyId(DEFAULT_FILTERS.companyId);
    setSiteId(DEFAULT_FILTERS.siteId);
    setServiceLine(DEFAULT_FILTERS.serviceLine);
    setDateFrom(DEFAULT_FILTERS.dateFrom);
    setDateTo(DEFAULT_FILTERS.dateTo);
    setPage(1);
  }, []);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const offset = (page - 1) * PAGE_SIZE;

  const exportFilters = useMemo(
    () => ({
      search: search.trim() || undefined,
      affiliation_id: affiliationId || undefined,
      company_id: companyId || undefined,
      site_id: siteId || undefined,
      service_line: serviceLine || undefined,
      invoice_kind: invoiceKind || undefined,
      date_from: dateFrom || undefined,
      date_to: dateTo || undefined
    }),
    [search, affiliationId, companyId, siteId, serviceLine, invoiceKind, dateFrom, dateTo]
  );

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const [listRes, summaryRes] = await Promise.all([
        invoiceService.list({
          ...exportFilters,
          limit: PAGE_SIZE,
          offset
        }),
        invoiceService.getSummary(exportFilters)
      ]);
      setRows(listRes?.success ? listRes.data || [] : []);
      setTotal(listRes?.pagination?.total ?? (listRes?.data?.length || 0));
      setSummary(summaryRes?.success ? summaryRes.data : null);
      if (!listRes?.success) showToast(listRes?.message || I.LOAD_ERROR, 'danger');
    } catch (e) {
      console.error(e);
      showToast(e.message || I.LOAD_ERROR, 'danger');
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [exportFilters, offset, showToast]);

  useEffect(() => {
    const t = setTimeout(() => loadList(), 200);
    return () => clearTimeout(t);
  }, [loadList]);

  useEffect(() => {
    setPage(1);
  }, [search, affiliationId, companyId, siteId, serviceLine, invoiceKind, dateFrom, dateTo]);

  const closeDetail = useCallback(() => {
    setSelectedId(null);
    setDetail(null);
  }, []);

  const openDetail = useCallback(
    async (id) => {
      setSelectedId(id);
      setDetailLoading(true);
      try {
        const res = await invoiceService.getById(id);
        if (res?.success) setDetail(res.data);
        else {
          showToast(res?.message || I.LOAD_ERROR, 'danger');
          setDetail(null);
        }
      } catch (e) {
        showToast(e.message || I.LOAD_ERROR, 'danger');
        setDetail(null);
      } finally {
        setDetailLoading(false);
      }
    },
    [showToast]
  );

  const handleExport = useCallback(
    async (format) => {
      if (exporting) return;
      setExporting(true);
      try {
        const res = await invoiceService.exportList(exportFilters);
        const invoices = res?.success ? res.data || [] : [];
        if (!invoices.length) {
          showToast(I.EXPORT_EMPTY, 'warning');
          return;
        }
        if (format === 'excel') {
          await exportInvoicesToExcel(invoices);
        } else {
          exportInvoicesToPdf(invoices);
        }
        showToast(I.EXPORT_OK, 'success');
      } catch (e) {
        console.error(e);
        showToast(e.message || I.EXPORT_ERROR, 'danger');
      } finally {
        setExporting(false);
      }
    },
    [exportFilters, exporting, showToast]
  );

  const detailItems = useMemo(() => detail?.items || [], [detail]);
  const isCoverageInvoice =
    String(detail?.invoice_kind || invoiceKind || '').toUpperCase() === INVOICE_KIND.COVERAGE;

  const affiliationOptions = useMemo(
    () =>
      [...affiliations].sort((a, b) =>
        String(a.label || a.name || '').localeCompare(String(b.label || b.name || ''), 'fr')
      ),
    [affiliations]
  );

  const companyOptions = useMemo(() => {
    const list = [...companies].sort((a, b) =>
      String(a.name || '').localeCompare(String(b.name || ''), 'fr')
    );
    if (!affiliationId) return list;
    return list.filter((co) => String(co.affiliation_id) === String(affiliationId));
  }, [affiliationId, companies]);

  const siteOptions = useMemo(
    () =>
      [...sites].sort((a, b) =>
        String(a.name || '').localeCompare(String(b.name || ''), 'fr')
      ),
    [sites]
  );

  return (
    <>
      <div className="ap-dash ap-inv-page">
        <AdminPersonnelPageHeader
          badge={I.BADGE}
          title={I.PAGE_TITLE}
          description={I.PAGE_SUBTITLE}
          actions={
            <Dropdown align="end">
              <Dropdown.Toggle
                variant="primary"
                size="sm"
                disabled={exporting || loading || total === 0}
                className="ap-inv-export-btn"
              >
                {exporting ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  <Icon name="download" size={14} />
                )}{' '}
                {I.BTN_EXPORT}
              </Dropdown.Toggle>
              <Dropdown.Menu className="ap-inv-export-menu">
                <Dropdown.Item onClick={() => handleExport('excel')}>
                  <Icon name="fileExcel" size={14} /> {I.BTN_EXPORT_EXCEL}
                </Dropdown.Item>
                <Dropdown.Item onClick={() => handleExport('pdf')}>
                  <Icon name="download" size={14} /> {I.BTN_EXPORT_PDF}
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          }
        />

        <section className="ap-inv-shell">
          <div className="ap-inv-kinds" role="tablist" aria-label="Type de facture">
            <button
              type="button"
              role="tab"
              aria-selected={invoiceKind === INVOICE_KIND.CARE}
              className={`ap-inv-kind-btn${invoiceKind === INVOICE_KIND.CARE ? ' is-active' : ''}`}
              onClick={() => {
                setInvoiceKind(INVOICE_KIND.CARE);
                closeDetail();
              }}
            >
              <span className="ap-inv-kind-btn__icon" aria-hidden="true">
                <Icon name="stethoscope" size={16} />
              </span>
              <span className="ap-inv-kind-btn__text">
                <span className="ap-inv-kind-btn__label">{I.TAB_CARE}</span>
                <span className="ap-inv-kind-btn__hint">{I.TAB_CARE_HINT}</span>
              </span>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={invoiceKind === INVOICE_KIND.COVERAGE}
              className={`ap-inv-kind-btn${invoiceKind === INVOICE_KIND.COVERAGE ? ' is-active' : ''}`}
              onClick={() => {
                setInvoiceKind(INVOICE_KIND.COVERAGE);
                closeDetail();
              }}
            >
              <span className="ap-inv-kind-btn__icon" aria-hidden="true">
                <Icon name="clipboard-list" size={16} />
              </span>
              <span className="ap-inv-kind-btn__text">
                <span className="ap-inv-kind-btn__label">{I.TAB_COVERAGE}</span>
                <span className="ap-inv-kind-btn__hint">{I.TAB_COVERAGE_HINT}</span>
              </span>
            </button>
          </div>

          {summary && (
            <div className="ap-inv-stats">
              <div className="ap-inv-stat ap-inv-stat--count">
                <span className="ap-inv-stat__icon" aria-hidden="true">
                  <Icon name="list" size={16} />
                </span>
                <div>
                  <span className="ap-inv-stat__value">{summary.total}</span>
                  <span className="ap-inv-stat__label">{I.STAT_COUNT}</span>
                </div>
              </div>
              <div className="ap-inv-stat ap-inv-stat--amount">
                <span className="ap-inv-stat__icon" aria-hidden="true">
                  <Icon name="chartBar" size={16} />
                </span>
                <div>
                  <span className="ap-inv-stat__value">{formatActPrice(summary.total_amount)}</span>
                  <span className="ap-inv-stat__label">{I.STAT_AMOUNT}</span>
                </div>
              </div>
            </div>
          )}

          <div className="ap-inv-toolbar">
            <div className="ap-inv-toolbar__row ap-inv-toolbar__row--primary">
              <div className="ap-inv-search">
                <span className="ap-inv-search__icon" aria-hidden="true">
                  <Icon name="search" size={15} />
                </span>
                <Form.Control
                  type="search"
                  className="ap-inv-search__input"
                  placeholder={I.SEARCH_PLACEHOLDER}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  aria-label="Rechercher une facture"
                />
              </div>
              <div className="ap-inv-toolbar__meta">
                <span className={`ap-inv-result-count${loading ? ' is-busy' : ''}`}>
                  {loading
                    ? I.LOADING
                    : total > 0
                      ? I.PAGINATION_LABEL(
                          offset + 1,
                          Math.min(offset + PAGE_SIZE, total),
                          total
                        )
                      : I.RESULT_COUNT(0)}
                </span>
                {hasFilters ? (
                  <button type="button" className="ap-inv-clear-btn" onClick={clearFilters}>
                    <Icon name="times" size={12} /> {I.CLEAR_FILTERS}
                  </button>
                ) : null}
              </div>
            </div>

            <div className="ap-inv-filters" role="group" aria-label="Filtres avancés">
              <label className="ap-inv-filter">
                <span className="ap-inv-filter__label">{I.FILTER_AFFILIATION}</span>
                <Form.Select
                  size="sm"
                  value={affiliationId}
                  onChange={(e) => {
                    const next = e.target.value;
                    setAffiliationId(next);
                    if (companyId) {
                      const stillValid = companies.some(
                        (co) =>
                          String(co.id) === String(companyId) &&
                          (!next || String(co.affiliation_id) === String(next))
                      );
                      if (!stillValid) setCompanyId('');
                    }
                  }}
                >
                  <option value="">{I.FILTER_AFFILIATION_ALL}</option>
                  {affiliationOptions.map((aff) => (
                    <option key={aff.id} value={aff.id}>
                      {aff.shortLabel || aff.label || aff.name}
                    </option>
                  ))}
                </Form.Select>
              </label>

              <label className="ap-inv-filter">
                <span className="ap-inv-filter__label">{I.FILTER_COMPANY}</span>
                <Form.Select
                  size="sm"
                  value={companyId}
                  onChange={(e) => setCompanyId(e.target.value)}
                >
                  <option value="">{I.FILTER_COMPANY_ALL}</option>
                  {companyOptions.map((co) => (
                    <option key={co.id} value={co.id}>
                      {co.name}
                    </option>
                  ))}
                </Form.Select>
              </label>

              <label className="ap-inv-filter">
                <span className="ap-inv-filter__label">{I.FILTER_SITE}</span>
                <Form.Select
                  size="sm"
                  value={siteId}
                  onChange={(e) => setSiteId(e.target.value)}
                >
                  <option value="">{I.FILTER_SITE_ALL}</option>
                  {siteOptions.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </Form.Select>
              </label>

              <label className="ap-inv-filter">
                <span className="ap-inv-filter__label">{I.FILTER_SERVICE}</span>
                <Form.Select
                  size="sm"
                  value={serviceLine}
                  onChange={(e) => setServiceLine(e.target.value)}
                >
                  {SERVICE_OPTIONS.map((opt) => (
                    <option key={opt.id || 'all'} value={opt.id}>
                      {opt.label}
                    </option>
                  ))}
                </Form.Select>
              </label>

              <div className="ap-inv-filter ap-inv-filter--period">
                <span className="ap-inv-filter__label">{I.FILTER_PERIOD}</span>
                <div className="ap-inv-period">
                  <Form.Control
                    type="date"
                    size="sm"
                    value={dateFrom}
                    max={dateTo || undefined}
                    onChange={(e) => setDateFrom(e.target.value)}
                    aria-label={I.FILTER_DATE_FROM}
                  />
                  <span className="ap-inv-period__sep" aria-hidden="true">
                    →
                  </span>
                  <Form.Control
                    type="date"
                    size="sm"
                    value={dateTo}
                    min={dateFrom || undefined}
                    onChange={(e) => setDateTo(e.target.value)}
                    aria-label={I.FILTER_DATE_TO}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="ap-inv-layout">
            <div className="ap-inv-list-wrap">
              {loading ? (
                <div className="ap-inv-state">
                  <Spinner animation="border" size="sm" />
                  <p className="ap-inv-state__title">{I.LOADING}</p>
                </div>
              ) : rows.length === 0 ? (
                <div className="ap-inv-state">
                  <span className="ap-inv-state__icon" aria-hidden="true">
                    <Icon name={hasFilters ? 'search' : 'inbox'} size={28} />
                  </span>
                  <p className="ap-inv-state__title">
                    {hasFilters
                      ? I.EMPTY_FILTERED_TITLE
                      : invoiceKind === INVOICE_KIND.COVERAGE
                        ? I.EMPTY_COVERAGE_TITLE
                        : I.EMPTY_TITLE}
                  </p>
                  <p className="ap-inv-state__text">
                    {hasFilters
                      ? I.EMPTY_FILTERED
                      : invoiceKind === INVOICE_KIND.COVERAGE
                        ? I.EMPTY_COVERAGE
                        : I.EMPTY}
                  </p>
                  {hasFilters ? (
                    <div className="ap-inv-state__actions">
                      <Button size="sm" variant="outline-secondary" onClick={clearFilters}>
                        {I.CLEAR_FILTERS}
                      </Button>
                    </div>
                  ) : null}
                </div>
              ) : (
                <table className="ap-inv-table">
                  <thead>
                    <tr>
                      <th>{I.COL_NUMBER}</th>
                      <th>{I.COL_DATE}</th>
                      <th>{I.COL_FULLNAME}</th>
                      <th>{I.COL_MATRICULE}</th>
                      <th>{I.COL_COMPANY}</th>
                      <th className="ap-inv-num">{I.COL_AMOUNT}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr
                        key={row.id}
                        className={selectedId === row.id ? 'is-selected' : ''}
                        onClick={() => openDetail(row.id)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            openDetail(row.id);
                          }
                        }}
                        tabIndex={0}
                        role="button"
                        aria-label={`${I.OPEN_HINT} ${row.invoice_number || ''}`}
                      >
                        <td>
                          <span className="ap-inv-num-pill">{row.invoice_number}</span>
                        </td>
                        <td className="ap-inv-date">{formatDate(row.service_date || row.created_at)}</td>
                        <td>
                          <div className="ap-inv-patient">{row.patient_name || '—'}</div>
                        </td>
                        <td className="ap-inv-mono">{row.member_matricule || '—'}</td>
                        <td>
                          <div className="ap-inv-company">{row.company_name || '—'}</div>
                          {row.affiliation_name ? (
                            <div className="ap-inv-sub">{row.affiliation_name}</div>
                          ) : null}
                        </td>
                        <td className="ap-inv-num ap-inv-amount">
                          {formatInvoiceAmount(row.total_amount)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {!loading && total > 0 && (
            <footer className="ap-inv-footer">
              <span>
                {I.PAGINATION_LABEL(offset + 1, Math.min(offset + PAGE_SIZE, total), total)}
              </span>
              {totalPages > 1 && (
                <nav className="ap-inv-page-nav" aria-label="Pagination factures">
                  <button
                    type="button"
                    className="ap-inv-page-btn"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    aria-label="Page précédente"
                  >
                    <Icon name="chevronLeft" size={14} />
                  </button>
                  <span className="ap-inv-page-label">
                    {page} / {totalPages}
                  </span>
                  <button
                    type="button"
                    className="ap-inv-page-btn"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                    aria-label="Page suivante"
                  >
                    <Icon name="chevronRight" size={14} />
                  </button>
                </nav>
              )}
            </footer>
          )}
        </section>
      </div>

      <AdminPersonnelModal
        show={Boolean(selectedId)}
        onHide={closeDetail}
        title={isCoverageInvoice ? I.SECTION_COVERAGE : I.SECTION_CARE}
        subtitle={detail?.invoice_number || undefined}
        icon="clipboard-list"
        size="lg"
        footer={
          <ModalFooterActions onCancel={closeDetail} cancelLabel={I.BTN_CLOSE_DETAIL} />
        }
      >
        {detailLoading || !detail ? (
          <div className="ap-inv-state ap-inv-state--modal">
            <Spinner animation="border" size="sm" />
          </div>
        ) : (
          <>
            <div className="ap-inv-detail-hero">
              <div className="ap-inv-detail-hero__main">
                <span
                  className={`ap-inv-kind-chip${isCoverageInvoice ? ' is-coverage' : ''}`}
                >
                  {isCoverageInvoice ? I.TAB_COVERAGE : I.TAB_CARE}
                </span>
                <h2 className="ap-inv-detail-patient">{detail.patient_name || '—'}</h2>
                <p className="ap-inv-detail-meta-line">
                  <span className="ap-inv-mono">{detail.member_matricule || '—'}</span>
                  <span aria-hidden="true"> · </span>
                  <span>{detail.company_name || '—'}</span>
                  {detail.affiliation_name ? (
                    <>
                      <span aria-hidden="true"> · </span>
                      <span>{detail.affiliation_name}</span>
                    </>
                  ) : null}
                </p>
              </div>
              <div className="ap-inv-detail-hero__ref">
                <span className="ap-inv-num-pill">{detail.invoice_number}</span>
                <span className="ap-inv-detail-date">
                  {formatDate(detail.service_date || detail.created_at)}
                </span>
              </div>
            </div>
            <div className="ap-inv-detail-scroll">
              <table className="ap-inv-lines ap-inv-lines--detail">
                <thead>
                  <tr>
                    <th>{I.COL_DESCRIPTION}</th>
                    <th className="ap-inv-num">{I.COL_AMOUNT}</th>
                  </tr>
                </thead>
                <tbody>
                  {detailItems.map((item) => (
                    <tr key={item.id}>
                      <td>
                        {item.label}
                        {!item.is_priced ? (
                          <span className="ap-inv-unpriced"> · {I.UNPRICED}</span>
                        ) : null}
                      </td>
                      <td className="ap-inv-num ap-inv-mono">
                        {formatInvoiceAmount(item.line_total)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="ap-inv-lines__section">
                    <td>{isCoverageInvoice ? I.SECTION_COVERAGE : I.SECTION_CARE}</td>
                    <td className="ap-inv-num ap-inv-mono">
                      {formatInvoiceAmount(detail.total_amount)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </>
        )}
      </AdminPersonnelModal>
    </>
  );
}
